import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Menu,
  Typography,
  alpha,
} from '@mui/material';
import { ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import type { BillingBudgetField } from '../../types/billing';
import { SEARCE_COLORS } from '../../theme';
import {
  buildBillingColumnGroups,
  getBillingColumnLabel,
  getMonthPrefixFromField,
  isMonthFieldVisible,
  MONTH_DISPLAY_LABELS,
  setMonthVisibility,
  type BillingColumnPresetId,
  type MonthPrefix,
} from '../../utils/billingTableConfig';

const PRESET_OPTIONS: { id: BillingColumnPresetId; label: string }[] = [
  { id: 'essentials', label: 'Essentials' },
  { id: 'full_year', label: 'Full year' },
  { id: 'all_columns', label: 'All columns' },
  { id: 'ownership_details', label: 'Ownership' },
];

export interface BillingColumnVisibilityMenuProps {
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  onPresetApply: (preset: BillingColumnPresetId) => void;
}

export function BillingColumnVisibilityMenu({
  columnVisibility,
  onColumnVisibilityChange,
  onPresetApply,
}: BillingColumnVisibilityMenuProps): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const groups = useMemo(() => buildBillingColumnGroups(), []);

  const toggleColumn = useCallback(
    (columnId: string, visible: boolean) => {
      onColumnVisibilityChange((prev) => {
        const next = { ...prev };
        if (visible) {
          delete next[columnId];
        } else {
          next[columnId] = false;
        }
        return next;
      });
    },
    [onColumnVisibilityChange],
  );

  const toggleGroup = useCallback(
    (columnIds: string[], show: boolean) => {
      onColumnVisibilityChange((prev) => {
        const next = { ...prev };
        columnIds.forEach((id) => {
          if (show) {
            delete next[id];
          } else {
            next[id] = false;
          }
        });
        return next;
      });
    },
    [onColumnVisibilityChange],
  );

  const toggleMonth = useCallback(
    (prefix: MonthPrefix, visible: boolean) => {
      onColumnVisibilityChange((prev) => setMonthVisibility(prefix, visible, prev));
    },
    [onColumnVisibilityChange],
  );

  const monthPrefixesInGroup = (columnIds: string[]): MonthPrefix[] => {
    const seen = new Set<MonthPrefix>();
    const prefixes: MonthPrefix[] = [];
    columnIds.forEach((id) => {
      const prefix = getMonthPrefixFromField(id as BillingBudgetField);
      if (prefix && !seen.has(prefix)) {
        seen.add(prefix);
        prefixes.push(prefix);
      }
    });
    return prefixes;
  };

  const isMonthGroup = (groupId: string) => groupId.startsWith('q');

  return (
    <>
      <Button
        size="small"
        variant="text"
        startIcon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          color: SEARCE_COLORS.primary,
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.8125rem',
          py: 0.25,
          px: 0.75,
          flexShrink: 0,
          '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.08) },
        }}
      >
        Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { maxHeight: 480, width: 300, p: 1 } }}
      >
        <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 700 }}>
          Presets
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1, pb: 1 }}>
          {PRESET_OPTIONS.map((p) => (
            <Button
              key={p.id}
              size="small"
              variant="text"
              onClick={() => {
                onPresetApply(p.id);
              }}
              sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
            >
              {p.label}
            </Button>
          ))}
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {groups.map((group) => {
          const monthGroup = isMonthGroup(group.id);
          const monthPrefixes = monthGroup ? monthPrefixesInGroup(group.columnIds) : [];
          const allVisible = monthGroup
            ? monthPrefixes.every((p) => isMonthFieldVisible(p, columnVisibility))
            : group.columnIds.every((id) => columnVisibility[id] !== false);
          const someVisible = monthGroup
            ? monthPrefixes.some((p) => isMonthFieldVisible(p, columnVisibility))
            : group.columnIds.some((id) => columnVisibility[id] !== false);

          return (
            <Box key={group.id} sx={{ px: 1, py: 0.5 }}>
              <FormControlLabel
                sx={{ m: 0, width: '100%' }}
                control={
                  <Checkbox
                    size="small"
                    checked={allVisible}
                    indeterminate={someVisible && !allVisible}
                    onChange={(e) => {
                      if (monthGroup) {
                        monthPrefixes.forEach((p) => toggleMonth(p, e.target.checked));
                      } else {
                        toggleGroup(group.columnIds, e.target.checked);
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    {group.label}
                  </Typography>
                }
              />
              <Box sx={{ pl: 3 }}>
                {monthGroup
                  ? monthPrefixes.map((prefix) => (
                      <FormControlLabel
                        key={prefix}
                        sx={{ m: 0, display: 'flex' }}
                        control={
                          <Checkbox
                            size="small"
                            checked={isMonthFieldVisible(prefix, columnVisibility)}
                            onChange={(e) => toggleMonth(prefix, e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            {MONTH_DISPLAY_LABELS[prefix]} (Spend / Budget)
                          </Typography>
                        }
                      />
                    ))
                  : group.columnIds.map((colId) => (
                      <FormControlLabel
                        key={colId}
                        sx={{ m: 0, display: 'flex' }}
                        control={
                          <Checkbox
                            size="small"
                            checked={columnVisibility[colId] !== false}
                            onChange={(e) => toggleColumn(colId, e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            {getBillingColumnLabel(colId)}
                          </Typography>
                        }
                      />
                    ))}
              </Box>
            </Box>
          );
        })}
      </Menu>
    </>
  );
}
